import { useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';

/**
 * A modal dialog, for the one thing on this portal that must not happen by
 * accident.
 *
 * ── WHY THIS APP HAS ONE AT ALL ──────────────────────────────────────
 *
 * Every other action here is reversible or harmless: change a password, add a
 * passkey, sign out a device. Closing the account is neither, so it is the only
 * place worth interrupting somebody to ask again — and an inline confirmation
 * that appears under a button is easy to click straight through, because the
 * cursor is already there.
 *
 * ── WHAT MAKES IT A DIALOG RATHER THAN A BOX ─────────────────────────
 *
 * `role="dialog"` plus `aria-modal` is the announcement. The rest is behaviour a
 * screen reader and a keyboard user will assume is present, and whose absence
 * shows up as being stuck rather than as anything visible:
 *
 *   FOCUS MOVES IN on open, to the dialog itself rather than to a control, so
 *   the heading and the body are read before anything is actionable. Focusing
 *   the confirm button instead would put a destructive control under the space
 *   bar of somebody who has not heard the question yet.
 *
 *   FOCUS RETURNS on close, to whatever opened it. Without this the tab order
 *   restarts at the top of the document and the reader has to find their place
 *   again, having just been told nothing happened.
 *
 *   ESCAPE CLOSES, and so does the backdrop. Both are the SAFE direction here:
 *   the dangerous action needs a typed confirmation and a deliberate press, and
 *   dismissing costs a person nothing. A dialog that traps somebody until they
 *   engage with an irreversible choice is the wrong trade.
 *
 * ── IT TAKES `open` RATHER THAN BEING MOUNTED CONDITIONALLY ─────────
 *
 * Every caller used to render `{open ? <Modal/> : null}`, which cannot fade
 * OUT: React removes the element on the same frame the flag flips, so there is
 * nothing left to animate. Jon saw the arrival land and the departure not
 * happen at all.
 *
 * So the flag comes in as a prop and this component owns its own removal. It
 * keeps rendering for the length of the exit and then stops. Doing it here
 * rather than at each call site is what makes EVERY close path animate — the
 * backdrop, Escape, a Cancel button inside the content, and a successful
 * submit all just flip the same boolean, and none of them has to know about a
 * duration.
 *
 * ── IT ARRIVES RATHER THAN APPEARING (Jon, 2026-09-02) ───────────────
 *
 * The dialog used to be simply present on the next frame, which Jon called
 * harsh — and the layout shift from the vanishing scrollbar made it worse,
 * because two things moved at once and neither was the dialog.
 *
 * The backdrop fades and the panel rises a few pixels while it fades. Short
 * (140ms) and small (8px): long enough to read as a thing arriving, short
 * enough that somebody who opens this ten times is not waiting for it.
 *
 * The exit is the same length and the reverse shape. Equal in both directions
 * on purpose: a dialog that leaves faster than it came reads as being dismissed
 * rather than closed, and one that leaves slower is in the way.
 *
 * ── THE KEYFRAMES ARE IN styles.css, UNLIKE EVERY OTHER STYLE HERE ───
 *
 * This app puts its styles inline. A keyframe cannot be expressed that way —
 * `animation` can name one, but `@keyframes` has to live in a stylesheet — so
 * these two are the exception, and they are in `styles.css` beside the
 * `prefers-reduced-motion` rule that switches them off. Somebody who has asked
 * their system for less motion gets the dialog with no movement at all, which
 * is the same dialog.
 *
 * ── FOCUS IS NOT TRAPPED IN A CYCLE, DELIBERATELY ────────────────────
 *
 * A full trap needs a list of focusable descendants and rules for Tab and
 * Shift+Tab at the ends, and it is the part that breaks quietly when the
 * contents change. `inert` on the rest of the page would be the modern answer;
 * jsdom does not implement it and Safari only recently did. Escaping the dialog
 * with Tab lands somebody in the page behind it, which is untidy and recoverable
 * — a broken trap is neither. Worth revisiting when `inert` is safe to rely on.
 */
/** Both animations, and the wait before unmounting. One number so they cannot
 *  disagree — a delay shorter than the animation cuts it off, and a longer one
 *  leaves an invisible dialog holding the page's scroll lock. */
const TRANSITION_MS = 140;

/** Has this person asked their system for less movement? Read at close time
 *  rather than cached, because it can change while the page is open. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // A jsdom without the shim, or an ancient browser. Motion is the safe
    // default to assume ABSENT: skipping the wait only makes the dialog close
    // instantly, which is what it did before any of this.
    return true;
  }
}

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  /** Rendering is this component's business, not the caller's — see the header.
   *  Flip this and every close path animates. */
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const titleId = 'modal-title';

  /**
   * `rendered` outlives `open` by the length of the exit; `leaving` is what
   * puts the exit animation on the DOM while it does.
   *
   * Two flags rather than one enum because they answer different questions and
   * are read in different places: one decides whether to return null at all,
   * the other decides a class name.
   */
  const [rendered, setRendered] = useState(open);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      setLeaving(false);
      return;
    }
    if (!rendered) return;
    /**
     * No wait at all when motion is unwanted. Holding the dialog on screen for
     * 140 invisible milliseconds would be the animation's cost without its
     * benefit, and it delays the focus return.
     */
    if (prefersReducedMotion()) {
      setRendered(false);
      return;
    }
    setLeaving(true);
    const timer = setTimeout(() => {
      setRendered(false);
      setLeaving(false);
    }, TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [open, rendered]);

  /**
   * THE HANDLER IS HELD IN A REF, AND THAT IS NOT A MICRO-OPTIMISATION.
   *
   * The effect below moves focus on mount and RESTORES IT ON CLEANUP. With
   * `onClose` in the dependency array, a caller passing an inline arrow — the
   * obvious way to write one — gets a new identity on every render, so the
   * effect tears down and re-runs on every render, and the cleanup yanks focus
   * back to whatever opened the dialog.
   *
   * The symptom is that typing into a field inside the modal drops every
   * character after the first: keystroke, re-render, focus stolen. Found by the
   * account#58 tests when the change-email form moved in here — the previous
   * caller had no fields, so nothing re-rendered and nothing was wrong.
   *
   * The ref keeps the effect's dependencies empty and always calls the latest
   * handler. The alternative is asking every caller to remember `useCallback`,
   * which is a rule that holds until somebody forgets it.
   */
  const close = useRef(onClose);
  close.current = onClose;

  /**
   * Keyed on `rendered`, NOT on `open`, and that is the load-bearing detail.
   *
   * The cleanup releases the page's scroll lock and its scrollbar padding. Tied
   * to `open` it would fire the instant the flag flipped — so the page behind
   * would jump sideways while the dialog was still visibly fading, which is the
   * exact jank the padding exists to prevent, just moved to a worse moment.
   *
   * Tied to `rendered` it fires when the dialog actually goes.
   */
  useEffect(() => {
    if (!rendered) return;
    const opener = document.activeElement as HTMLElement | null;
    dialog.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);

    /**
     * The page behind must not scroll under the dialog. Restored exactly rather
     * than set to '', so a stylesheet that sets an overflow keeps it.
     *
     * ── AND THE SCROLLBAR'S WIDTH IS HELD (Jon, 2026-09-02) ────────────
     *
     * Hiding the overflow removes the scrollbar, the content widens by its
     * width, and everything behind the dialog jumps sideways as it opens —
     * then jumps back as it closes. On a screen that also has a contents rail
     * in the margin, that shift is the most visible thing about the whole
     * transition, and it reads as the page breaking rather than as a dialog
     * arriving.
     *
     * `scrollbar-gutter` would be the modern answer and has to be set on the
     * page in advance, reserving the space at all times; padding the body by
     * the width that just disappeared costs nothing when there is no scrollbar
     * (an overlay one, a touch device, a short page) because the difference is
     * then zero.
     */
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) {
      const current = Number.parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${current + gap}px`;
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
      opener?.focus?.();
    };
  }, [rendered]);

  if (!rendered) return null;

  return (
    <div
      onClick={onClose}
      className={`rw-modal-backdrop${leaving ? ' rw-modal-leaving' : ''}`}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 50,
      }}
    >
      <div
        ref={dialog}
        className={`rw-modal-panel${leaving ? ' rw-modal-leaving' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        // Or a click anywhere inside the dialog would close it, including a
        // click that lands on the padding beside a field.
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: C.surface,
          border: `1px solid ${C.inputBorder}`,
          borderRadius: 8,
          padding: '1.5rem',
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        <h2 id={titleId} style={{ margin: '0 0 1rem', fontSize: '1.15rem' }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
